import React, { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import DrawerAppBar from "../../../../components/mobile/Navbar";
import { ListReceipts } from "../../../../../domain/usecases/list-receipts";
import {
  Receipt,
  ReceiptStatus,
} from "../../../../../domain/models/receipt-model";
import { Main } from "./Components/Main";
import { Collector } from "./Components/Collector";
import { List } from "./Components/List";
import { Report } from "./Components/Report";
import { LoadProductList } from "../../../../../domain/usecases/load-product-list";
import { MainSkeleton } from "./Components/MainSkeleton";
import { Container } from "@mui/system";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { StoreItem } from "../../../../../domain/models/storage-item";
import { CustomDialog } from "../../../../components/Dialog";
import { CheckReceipt } from "../../../../../domain/usecases/check-receipt";
import { LoadReceiptCheck } from "../../../../../domain/usecases/load-receipt-check";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "../../../hooks";
import { makeDbLoadProductListByManyCodes } from "../../../../../main/factories/usecases/load-product-list-by-many-codes-factory";

type Props = {
  listReceipts: ListReceipts;
  loadProductList: LoadProductList;
  checkReceipt: CheckReceipt;
  loadReceiptCheck: LoadReceiptCheck;
};

type Pages = "main" | "collector" | "list" | "report" | "error";

export const CheckReceipts: React.FC<Props> = ({
  listReceipts,
  loadProductList,
  checkReceipt,
  loadReceiptCheck,
}) => {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [isFinishDialogOpen, setIsFinishDialogOpen] = useState<boolean>(false);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [currentPage, setCurrentPage] = useState<Pages>("main");
  const [currentReceipt, setCurrentReceipt] = useState<Receipt>(receipts[0]);
  const [checkedList, setCheckedList] = useState<StoreItem[]>([]);
  const [finishedLoading, setFinishedLoading] = useState<boolean>(false);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleError = useErrorHandler((error) => {
    setCurrentPage("error");
  });

  useEffect(() => {
    async function fetchData() {
      const warehouseString = localStorage.getItem("currentWarehouse");
      const warehouse = warehouseString
        ? (JSON.parse(warehouseString) as {
            id: number;
            name: string;
          } | null)
        : undefined;
      try {
        const receipts = await listReceipts.list(warehouse!.id, {
          status: [
            ReceiptStatus.notChecked,
            ReceiptStatus.missingProduct,
            ReceiptStatus.pending,
            ReceiptStatus.unexpectedProduct,
          ],
        });
        setReceipts(receipts);
      } catch (error) {
        handleError(error as Error);
      } finally {
        setFinishedLoading(true);
      }
    }
    fetchData();
  }, []);

  const getTitle = () => {
    if (currentPage === "main") return "Conferências de Entrada";
    if (currentPage === "collector") return "Dados da Mercadoria";
    if (currentPage === "list") return `Recebimento ${currentReceipt.name}`;
    if (currentPage === "report") return `Relatório Final`;
  };

  const handleBackClick = () => {
    if (currentPage === "main") return navigate("/mobile/check");
    if (currentPage === "error") return setCurrentPage("main");
    if (currentPage === "collector") return setIsCancelDialogOpen(true);
    if (currentPage === "list") return setCurrentPage("collector");
    if (currentPage === "report") {
      setCurrentPage("main");
    }
  };

  return (
    <Stack>
      <CustomDialog
        isOpen={isCancelDialogOpen}
        title="Deseja continuar?"
        content="Ao voltar para a página anterior, toda sua contagem será perdida."
        onYes={() => {
          setCheckedList([]);
          setCurrentPage("main");
          setIsCancelDialogOpen(false);
        }}
        onNo={() => {
          setIsCancelDialogOpen(false);
        }}
        handleClose={() => {
          setIsCancelDialogOpen(false);
        }}
      />
      <CustomDialog
        isOpen={isFinishDialogOpen}
        title="Erro ao finalizar operação"
        content="Ocorreu um erro. Por favor, tente novamente."
        onYes={() => {
          setIsFinishDialogOpen(false);
        }}
        handleClose={() => {
          setIsFinishDialogOpen(false);
        }}
      />
      <DrawerAppBar title={getTitle()} onBack={handleBackClick} />
      {currentPage === "main" &&
        (receipts.length === 0 && !finishedLoading ? (
          <MainSkeleton />
        ) : (
          <Main
            receipts={receipts}
            onClick={(id: number) => {
              const current = receipts.filter((receipt) => receipt.id === id);
              setCurrentReceipt(current[0]);
              if (current[0].status === ReceiptStatus.checked) {
                setCurrentPage("report");
              } else {
                setCurrentPage("collector");
              }
            }}
            onSeeReport={(id: number) => {
              const current = receipts.filter((receipt) => receipt.id === id);
              setCurrentReceipt(current[0]);
              setCurrentPage("report");
            }}
          />
        ))}
      {currentPage === "collector" && (
        <Collector
          parentList={checkedList}
          loadProductList={loadProductList}
          onFinish={(list) => {
            setCheckedList(list);
            setCurrentPage("list");
          }}
        />
      )}

      {currentPage === "list" && (
        <List
          currentReceipt={currentReceipt}
          list={checkedList}
          checkReceipt={checkReceipt}
          isLoading={isListLoading}
          onFinish={async () => {
            const finalList = checkedList.map((item) => ({
              ...item,
              amount: Number(
                (item.amount * (item?.packageAmount || 1)).toFixed(2)
              ),
            }));
            setIsListLoading(true);
            try {
              const updatedReceipt = await checkReceipt.check(
                currentReceipt.id!,
                finalList
              );
              if (updatedReceipt) return setCurrentPage("report");
            } catch (error) {
              setIsFinishDialogOpen(true);
              setIsListLoading(false);
            } finally {
              setIsListLoading(false);
            }
          }}
        />
      )}
      {currentPage === "report" && (
        <Report
          loadReceiptCheck={loadReceiptCheck}
          loadProductListByManyCodes={makeDbLoadProductListByManyCodes()}
          receiptId={currentReceipt.id!}
          onError={() => {
            setCurrentPage("error");
          }}
        />
      )}
      {currentPage === "error" && (
        <Container
          sx={{
            width: "100%",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <WarningRoundedIcon fontSize="large" />
          <Typography fontWeight="bold" fontSize="26px">
            Erro ao carregar
          </Typography>
        </Container>
      )}
    </Stack>
  );
};
